import axios from "axios";
import Config from "config";
import { logger } from "helpers/logger";
import urlJoin from "helpers/urljoin";
import { getPersonalizedSearchSuggestions } from "services/searchSuggestionsService";
import { getGeoDataForSearchSuggestions } from "@Utils/getGeoData";

let cachedSearchSuggestions: any = null;
let cachedIsPersonalized = false;
let inFlightRequest: Promise<any> | null = null;
let inFlightIsPersonalized = false;

interface GetSearchSuggestionsOptions {
  isPersonalizedSearchEnabled?: boolean;
}

export const getSearchSuggestions = async (options: GetSearchSuggestionsOptions = {}) => {
  const { isPersonalizedSearchEnabled = false } = options;

  // If personalized is now enabled but the cache holds non-personalized data, invalidate
  if (cachedSearchSuggestions && isPersonalizedSearchEnabled && !cachedIsPersonalized) {
    cachedSearchSuggestions = null;
  }

  if (cachedSearchSuggestions) {
    return cachedSearchSuggestions;
  }

  // Deduplicate in-flight requests only if request type matches
  if (inFlightRequest && inFlightIsPersonalized === isPersonalizedSearchEnabled) {
    return inFlightRequest;
  }

  inFlightIsPersonalized = isPersonalizedSearchEnabled;
  inFlightRequest = (async () => {
    try {
      // Try personalized suggestions first if geo-data is available and feature flag is on
      if (isPersonalizedSearchEnabled && typeof window !== "undefined") {
        const geoData = await getGeoDataForSearchSuggestions();
        if (geoData.sourceCountry) {
          const personalizedRes = await getPersonalizedSearchSuggestions(geoData);
          if (personalizedRes !== "error" && personalizedRes?.data?.data?.personalized === true) {
            cachedSearchSuggestions = personalizedRes;
            cachedIsPersonalized = true;
            return personalizedRes;
          }
        }
      }

      // Fallback to existing search_config endpoint
      const url = urlJoin(Config.API_URL, "/api/v0/regions/search_config");
      const res = await axios.get(url);
      cachedSearchSuggestions = res;
      cachedIsPersonalized = false;
      return res;
    } catch (error) {
      logger.error("Error fetching search suggestions data:", error);
      return "error";
    } finally {
      inFlightRequest = null;
    }
  })();

  return inFlightRequest;
};

export const getSearchResult = async (
  searchName: string,
  isWhitelabel: boolean,
  isRestrictedSearchEnabled: boolean,
  partner_id: string,
) => {
  try {
    const url = urlJoin(Config.API_URL, `/api/v0/homes/search`);
    const res = await axios.get(url, {
      params: {
        limit: 10,
        sort_key: "search_name",
        sort_order: "desc",
        states: "active",
        search_name: searchName,
        ...(isWhitelabel && { partner_id }),
        ...(isWhitelabel && { global_search: !isRestrictedSearchEnabled }),
        use_java_api: true,
      },
      withCredentials: true,
    });
    return res;
  } catch (error) {
    logger.error("Error fetching search suggestions data:", error);
    return "error";
  }
};
